import ChevronRight from '@ixigo/icons/ChevronRight';
import Collapse from '@ixigo/iui/Collapse';
import * as Timeline from '@ixigo/iui/Timeline';
import Typography from '@ixigo/iui/Typography';
import { useRef, useState } from 'react';
import type { RefundStatus, RefundTimelineItem } from '@/api';

type AlertColor = 'success' | 'warning' | 'critical' | 'info';

export const statusColorMap: Record<
	RefundStatus,
	{ color: Timeline.TimelineItemProps['color']; severity: AlertColor }
> = {
	COMPLETED: { color: 'success', severity: 'success' },
	IN_PROGRESS: { color: 'success', severity: 'success' },
	REJECTED: { color: 'critical', severity: 'critical' },
	INITIATED: { color: 'neutral', severity: 'warning' },
};

const findLastItem = <T,>(
	array: T[],
	predicate: (value: T) => boolean,
): T | undefined => {
	for (let i = array.length - 1; i >= 0; i--) {
		const item = array[i];
		if (item !== undefined && predicate(item)) {
			return item;
		}
	}
	return undefined;
};

export const RefundTimeline = ({
	timelineItems,
}: {
	timelineItems: RefundTimelineItem[];
}) => {
	const [isExpanded, setIsExpanded] = useState(true);
	const [showFirstConnector, setShowFirstConnector] = useState(true);
	const firstTimelineItem = useRef(timelineItems[0]);
	const otherTimelineItems = timelineItems.slice(1);

	return (
		<Timeline.Root
			orientation="vertical"
			contentPosition="end"
			className="w-full overflow-hidden"
		>
			{firstTimelineItem && (
				<Timeline.Item
					key={firstTimelineItem.current?.title}
					className="relative"
					completed={firstTimelineItem.current?.status === 'COMPLETED'}
					color={
						firstTimelineItem.current?.status
							? statusColorMap[firstTimelineItem.current?.status].color
							: 'neutral'
					}
				>
					<Timeline.Separator hideConnector={!showFirstConnector} />
					<Timeline.Content
						label={
							<div className="flex items-center justify-between gap-2">
								<Typography variant="body-md">
									{firstTimelineItem.current?.title}
								</Typography>
								<ChevronRight
									className={`transition-transform duration-300 ${
										isExpanded ? '-rotate-90' : 'rotate-90'
									}`}
									onClick={() => setIsExpanded(!isExpanded)}
								/>
							</div>
						}
						subText={firstTimelineItem.current?.subtitle}
						className={`duration-600 transition-all ease-in-out ${
							isExpanded ? '!pb-15' : ''
						}`}
					/>
				</Timeline.Item>
			)}
			<Collapse
				in={isExpanded}
				onExited={() => {
					setShowFirstConnector(false);
					firstTimelineItem.current =
						findLastItem(
							timelineItems,
							(item) => item.status === 'IN_PROGRESS',
						) || otherTimelineItems[otherTimelineItems.length - 1];
				}}
				onEnter={() => {
					setShowFirstConnector(true);
					firstTimelineItem.current = timelineItems[0];
				}}
			>
				{otherTimelineItems.map((item, index) => (
					<Timeline.Item
						key={item.title}
						className="relative"
						completed={item.status === 'COMPLETED'}
						color={item.status ? statusColorMap[item.status].color : 'neutral'}
					>
						<Timeline.Separator
							hideConnector={otherTimelineItems.length - 1 === index}
						/>
						<Timeline.Content label={item.title} subText={item.subtitle} />
					</Timeline.Item>
				))}
			</Collapse>
		</Timeline.Root>
	);
};
